/**
 * Settings page component
 */

import { useState } from 'react';
import { Layout } from '../components/Layout';
import { settingsApi } from '../services/api';

export function Settings() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [vpnApiKey, setVpnApiKey] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUpdatingVpnKey, setIsUpdatingVpnKey] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setIsChangingPassword(true);

    const response = await settingsApi.updatePassword(currentPassword, newPassword);

    if (response.success) {
      showToast('Password changed successfully!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      showToast(response.error?.message || 'Failed to change password', 'error');
    }

    setIsChangingPassword(false);
  };

  const handleUpdateVpnKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingVpnKey(true);

    const response = await settingsApi.updateVpnApiKey(vpnApiKey);

    if (response.success) {
      showToast('VPN API key updated successfully!', 'success');
      setVpnApiKey('');
    } else {
      showToast(response.error?.message || 'Failed to update VPN API key', 'error');
    }

    setIsUpdatingVpnKey(false);
  };

  return (
    <Layout>
      <div className="max-w-3xl space-y-6">
        {/* Toast */}
        {toast && (
          <div className={`p-4 rounded-lg border ${
            toast.type === 'success' 
              ? 'bg-green-900/50 border-green-700' 
              : 'bg-red-900/50 border-red-700'
          }`}>
            <p className={toast.type === 'success' ? 'text-green-300' : 'text-red-300'}>
              {toast.message}
            </p>
          </div>
        )}

        {/* Change Password */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            {passwordError && (
              <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg">
                <p className="text-sm text-red-300">{passwordError}</p>
              </div>
            )}

            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter current password"
                required
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter new password"
                required
                minLength={8}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>

            <button
              type="submit"
              disabled={isChangingPassword}
              className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* VPN API Key */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-2">VPN Detection API</h2>
          <p className="text-sm text-gray-400 mb-6">
            Configure the API key for the VPN detection service. This is used to check if visitors are using VPN, proxy, or Tor connections.
          </p>

          <form onSubmit={handleUpdateVpnKey} className="space-y-4">
            <div>
              <label htmlFor="vpnApiKey" className="block text-sm font-medium text-gray-300 mb-2">
                API Key
              </label>
              <input
                id="vpnApiKey"
                type="password"
                value={vpnApiKey}
                onChange={(e) => setVpnApiKey(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter VPN API key"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isUpdatingVpnKey || !vpnApiKey}
              className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingVpnKey ? 'Updating...' : 'Update API Key'}
            </button>
          </form>
        </div>

        {/* System Info */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">System Information</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-700">
              <span className="text-gray-400">Application</span>
              <span className="text-white">FakeIP Media Grabber</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-700">
              <span className="text-gray-400">Version</span>
              <span className="text-white">1.0.0</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-700">
              <span className="text-gray-400">Environment</span>
              <span className="text-white">Production</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-400">Admin Panel</span>
              <span className="text-white">React + Vite + TailwindCSS</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Settings;
